import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { NOTIFICATION_TYPES } from '../constants/notifications';

interface PrefRow {
  notification_type: string;
  enabled: boolean;
}

/**
 * Public, no-login email preferences page reached from the "Manage email
 * preferences or unsubscribe" link in app emails. Authenticated entirely by the
 * signed `token` query param (see backend create_unsubscribe_token); the
 * /api/notifications/unsubscribe endpoints resolve it to a user.
 */
const UnsubscribePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [errorMsg, setErrorMsg] = useState('');
  const [email, setEmail] = useState('');
  const [prefs, setPrefs] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMsg('This unsubscribe link is missing its token.');
      return;
    }
    const load = async () => {
      try {
        const res = await axios.get(
          `/notifications/unsubscribe?token=${encodeURIComponent(token)}`,
        );
        const map: Record<string, boolean> = {};
        (res.data.preferences as PrefRow[]).forEach((p) => {
          map[p.notification_type] = p.enabled;
        });
        setPrefs(map);
        setEmail(res.data.email || '');
        setStatus('ready');
      } catch (e: any) {
        setStatus('error');
        setErrorMsg(
          e?.response?.data?.detail || 'This unsubscribe link is invalid or has expired.',
        );
      }
    };
    load();
  }, [token]);

  const save = useCallback(
    async (next: Record<string, boolean>) => {
      setSaving(true);
      setSavedMsg('');
      try {
        await axios.patch(
          `/notifications/unsubscribe?token=${encodeURIComponent(token)}`,
          { preferences: next },
        );
        setPrefs(next);
        setSavedMsg('Your email preferences have been saved.');
      } catch (e: any) {
        setSavedMsg(
          e?.response?.data?.detail || 'Could not save your preferences. Please try again.',
        );
      } finally {
        setSaving(false);
      }
    },
    [token],
  );

  const toggle = (id: string) => setPrefs((p) => ({ ...p, [id]: !(p[id] ?? true) }));

  const unsubscribeAll = () => {
    const allOff: Record<string, boolean> = {};
    NOTIFICATION_TYPES.forEach((t) => {
      allOff[t.id] = false;
    });
    save(allOff);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-start justify-center px-4 py-12">
      <div className="w-full max-w-lg bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-100 dark:border-gray-700/50 p-6 sm:p-8">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Email preferences</h1>

        {status === 'loading' && (
          <div className="flex justify-center py-10">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
          </div>
        )}

        {status === 'error' && (
          <p className="mt-4 text-sm text-gray-600 dark:text-gray-300">{errorMsg}</p>
        )}

        {status === 'ready' && (
          <>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Choose which emails {email ? <span className="font-medium">{email}</span> : 'you'} should
              receive. Turn one off to stop just that kind; turn them all off to stop everything.
            </p>

            <div className="mt-6 space-y-3">
              {NOTIFICATION_TYPES.map((type) => {
                const on = prefs[type.id] ?? true;
                return (
                  <div
                    key={type.id}
                    className="flex items-center justify-between p-4 border border-gray-100 dark:border-gray-700/50 rounded-xl"
                  >
                    <div className="flex-1 mr-4">
                      <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                        {type.label}
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{type.description}</p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={on}
                      aria-label={`Toggle ${type.label}`}
                      onClick={() => toggle(type.id)}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 ${
                        on ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-600'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          on ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                );
              })}
            </div>

            {savedMsg && (
              <p className="mt-4 text-sm text-primary-700 dark:text-primary-400" role="status">
                {savedMsg}
              </p>
            )}

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={() => save(prefs)}
                disabled={saving}
                className="inline-flex items-center justify-center px-4 py-2.5 rounded-md bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium disabled:opacity-60 min-h-[44px]"
              >
                {saving ? 'Saving…' : 'Save preferences'}
              </button>
              <button
                type="button"
                onClick={unsubscribeAll}
                disabled={saving}
                className="inline-flex items-center justify-center px-4 py-2.5 rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-60 min-h-[44px]"
              >
                Unsubscribe from all
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default UnsubscribePage;
